import { NumericTextBox, NumericTextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { GridFilterCellProps } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import React, { useState } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns/dist/npm/DropDownList/DropDownList";
import { DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";

type CustomDecimalFilterCellProps = {
  gridFilterCellProps: GridFilterCellProps;
  format: KendoReactNumericFilterFormat;
};

type NumericFilterOperatorType = {
  text: string;
  operator: string;
};

type KendoReactNumericFilterFormat = "#";

const numericFilterOperators: NumericFilterOperatorType[] = [
  { text: "Is equal to", operator: "eq" },
  { text: "Is not equal to", operator: "neq" },
  { text: "Is greater than or equal", operator: "gte" },
  { text: "Is greater than", operator: "gt" },
  { text: "Is less than or equal", operator: "lte" },
  { text: "Is less than", operator: "lt" },
  { text: "Is null", operator: "isnull" },
  { text: "Is not null", operator: "isnotnull" },
];

const CustomDecimalFilterCell = ({ gridFilterCellProps, format }: CustomDecimalFilterCellProps) => {
  const { onChange: filterCellPropsOnChange } = gridFilterCellProps;

  const [filterCellValue, setFilterCellValue] = useState<number | null>(null);
  const [isFilterActive, setIsFilterActive] = useState<boolean>(false);
  const [selectedOperator, setSelectedOperator] = useState<NumericFilterOperatorType>(
    numericFilterOperators[0]
  );

  const numericTextBoxOnChange = (event: NumericTextBoxChangeEvent) => {
    const { value: filterValue } = event.target;

    // Reset filter if all cell values are deleted
    if (filterValue === null) {
      setIsFilterActive(false);
      setFilterCellValue(null);

      filterCellPropsOnChange({
        value: null,
        operator: filterValue ? selectedOperator.operator : "",
        syntheticEvent: event.syntheticEvent,
      });

      return;
    }

    if (String(filterValue).startsWith("0")) {
      setFilterCellValue(null);
      setIsFilterActive(false);
    } else {
      setFilterCellValue(filterValue);
      setIsFilterActive(true);

      filterCellPropsOnChange({
        value: filterValue,
        operator: filterValue ? selectedOperator.operator : "",
        syntheticEvent: event.syntheticEvent,
      });
    }
  };

  const onButtonClearClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    setFilterCellValue(null);
    setIsFilterActive(false);
    setSelectedOperator(numericFilterOperators[0]);

    filterCellPropsOnChange({
      value: "",
      operator: "",
      syntheticEvent: event,
    });
  };

  const dropdownlistOnChange = (event: DropDownListChangeEvent) => {
    const operator = event.value as NumericFilterOperatorType;
    setSelectedOperator(operator);
    setIsFilterActive(true);
  };

  return (
    <div className="k-filtercell">
      <NumericTextBox
        style={{ marginRight: "4px" }}
        onChange={numericTextBoxOnChange}
        format={format}
        value={filterCellValue}
        min={1}
      />

      <DropDownList
        data={numericFilterOperators}
        textField="text"
        dataItemKey="operator"
        onChange={dropdownlistOnChange}
        value={numericFilterOperators.find((item) => item.operator === selectedOperator.operator)}
        popupSettings={{ width: "180px" }}
        className="k-dropdownlist k-picker k-dropdown-operator k-picker-md k-rounded-md k-picker-solid"
        iconClassName="k-icon k-i-filter k-button-icon"
        style={{ marginRight: "4px" }}
      />

      <Button
        title="Clear"
        className="k-button-md"
        onClick={onButtonClearClick}
        disabled={!isFilterActive}
      >
        <span
          className={`k-icon k-i-filter-clear k-button-icon ${
            !isFilterActive ? "k-disabled" : "k-clear-button-visible"
          }`}
        />
      </Button>
    </div>
  );
};

export const customDecimalFilterCell = (
  props: GridFilterCellProps,
  format: KendoReactNumericFilterFormat
) => {
  return <CustomDecimalFilterCell gridFilterCellProps={props} format={format} />;
};
